// Approved public collection routes, shared by entry and same-origin referrer gates.
function publicCollectionPathAllowed(pathname) {
  return [
    "/collections/rings",
    "/collections/ancient-roman-rings",
    "/collections/medieval-crusader-rings",
    "/collections/highlights",
    "/collections/new-arrivals",
    "/collections/ring-drop-2026-10-10"
].includes(pathname);
}

// ../../consent-gate.mjs
function recordingPolicy(input) {
  if (!input?.enabled) return { allowed: false, reason: "disabled" };
  if (!input.privacyApiPresent) return { allowed: false, reason: "privacy-api-missing" };
  if (input.analyticsConsent !== "yes" || input.analyticsProcessingAllowed !== true) {
    return { allowed: false, reason: "analytics-not-explicitly-allowed" };
  }
  let url;
  try {
    url = new URL(input.url);
  } catch {
    return { allowed: false, reason: "invalid-url" };
  }
  if (url.origin !== "https://auroraantiqua.com") return { allowed: false, reason: "wrong-origin" };
  if (url.search || url.hash) return { allowed: false, reason: "url-not-canonical" };
  if (url.pathname === "/") return { allowed: true, surface: "home" };
  if (publicCollectionPathAllowed(url.pathname)) return { allowed: true, surface: url.pathname === "/collections/rings" ? "rings" : "public-collection" };
  if (/^\/products\/[a-z0-9-]+\/?$/.test(url.pathname) && input.productEarlyAccess === false) {
    return { allowed: true, surface: "public-product" };
  }
  return { allowed: false, reason: "surface-not-approved" };
}

// ../lifecycle-prototype.mjs
function createPilotLifecycle(deps) {
  let permitted = false;
  let disposed = false;
  let active = false;
  let generation = 0;
  let sdk;
  let loading;
  const policy = () => {
    try {
      return recordingPolicy(deps.readCurrentState());
    } catch {
      return { allowed: false, reason: "state-read-failed" };
    }
  };
  const allowed = () => permitted && !disposed && policy().allowed;
  function block() {
    permitted = false;
    generation++;
    try {
      deps.abortTransports();
    } catch {
    }
    if (sdk && active) {
      try {
        sdk.consentv2({ ad_Storage: "denied", analytics_Storage: "denied" });
      } catch {
      }
      try {
        sdk.stop();
      } catch {
      }
      active = false;
      try {
        deps.clearOnlyOwnedClarityStorage();
      } catch {
      }
    }
  }
  async function sync() {
    if (disposed || !policy().allowed) {
      block();
      return false;
    }
    permitted = true;
    if (active) return true;
    const expectedGeneration = generation;
    loading ?? (loading = Promise.resolve().then(() => deps.loadInertSdk()));
    let loaded;
    try {
      loaded = await loading;
    } catch {
      block();
      loading = void 0;
      return false;
    }
    if (generation !== expectedGeneration || !allowed()) return false;
    if (active) return true;
    if (loaded.capabilities?.completeStartGuard !== true) {
      block();
      return false;
    }
    sdk = loaded;
    const recordingGeneration = generation;
    try {
      sdk.installAuditedStartGuard(allowed);
      if (deps.applyStrictMaskBeforeStart() !== true || !allowed()) {
        block();
        return false;
      }
      // Old Clarity consent cookies can turn SDK track:false back on during start.
      // Remove only this integration's owned identifiers before metadata reads them.
      deps.clearOnlyOwnedClarityStorage();
      if (!allowed()) {
        block();
        return false;
      }
      active = true;
      sdk.start({
        projectId: deps.projectId,
        track: false,
        content: true,
        mask: ["html"],
        unmask: [],
        cookies: [],
        report: null,
        fraud: false,
        diagnostics: false,
        modules: [],
        fallback: null,
        // upload callback exists in the official SDK Config type. This closes
        // the SDK's stop-flush path, without globally patching fetch/XHR.
        upload(payload) {
          if (!allowed() || generation !== recordingGeneration) return;
          deps.sendAcceptedPayload(payload, {
            stillAllowed: () => allowed() && generation === recordingGeneration
          });
        }
      });
      sdk.consentv2({ ad_Storage: "denied", analytics_Storage: "denied" });
      return true;
    } catch {
      block();
      return false;
    }
  }
  return {
    sync,
    // Bind synchronously BEFORE pushState/replaceState and DOM route changes.
    // Re-evaluate only AFTER the final URL and PDP EA marker are authoritative.
    beforeRouteChange: block,
    revoke: block,
    dispose() {
      block();
      disposed = true;
    },
    status: () => ({ active, permitted, generation, disposed })
  };
}

// abortable-transport.mjs
function createAbortableTransport({ fetchImpl, endpoint, isAllowed, onStatus = () => {
}, onFatal = () => {
} }) {
  const target = new URL(endpoint);
  if (target.protocol !== "https:" || target.hostname !== "e.clarity.ms" || target.pathname !== "/collect" || target.search || target.hash) {
    throw new Error("Unexpected Clarity endpoint");
  }
  const pending = /* @__PURE__ */ new Set();
  let started = 0, completed = 0, responses = 0, lastHttpStatus = null, lastErrorName = "none", failed = false;
  async function send(payload, { stillAllowed }) {
    if (failed || !isAllowed() || !stillAllowed()) return;
    const controller = new AbortController();
    pending.add(controller);
    try {
      if (!isAllowed() || !stillAllowed()) return;
      started++;
      onStatus({ started, completed, responses, lastHttpStatus, pending: pending.size });
      const response = await fetchImpl(target.href, {
        method: "POST",
        body: payload,
        credentials: "omit",
        mode: "cors",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        signal: controller.signal,
        keepalive: false,
        cache: "no-store",
        redirect: "error",
        referrerPolicy: "no-referrer"
      });
      responses++;
      lastHttpStatus = response.status;
      if (!response.ok && isAllowed() && stillAllowed()) {
        failed = true;
        onFatal({ reason: "collector-http-failure" });
      }
      onStatus({ status: response.status, ok: response.ok, started, completed, responses, lastHttpStatus, pending: pending.size });
    } catch (error) {
      lastErrorName = ["AbortError", "TypeError", "NetworkError"].includes(error?.name) ? error.name : "OtherError";
      if (error?.name !== "AbortError" && isAllowed() && stillAllowed()) {
        failed = true;
        onFatal({ reason: "collector-network-failure" });
      }
      onStatus({ status: null, ok: false, aborted: error?.name === "AbortError", started, completed, responses, lastHttpStatus, pending: pending.size });
    } finally {
      pending.delete(controller);
      completed++;
      onStatus({ started, completed, responses, lastHttpStatus, pending: pending.size });
    }
  }
  return {
    send,
    abortAll() {
      for (const controller of pending) controller.abort();
      pending.clear();
    },
    pendingCount: () => pending.size,
    counters: () => ({ started, completed, responses, lastHttpStatus, lastErrorName, circuitOpen: failed })
  };
}

// browser-pilot.mjs
function referrerAllowed(referrer) {
  if (!referrer) return true;
  try {
    const url = new URL(referrer);
    return url.origin === "https://auroraantiqua.com" && !url.search && !url.hash && (url.pathname === "/" || publicCollectionPathAllowed(url.pathname));
  } catch {
    return false;
  }
}
function applyStrictDomMask(doc) {
  if (!doc.documentElement) return false;
  doc.documentElement.setAttribute("data-clarity-mask", "true");
  for (const element of doc.querySelectorAll("[data-clarity-unmask]")) element.removeAttribute("data-clarity-unmask");
  return true;
}
function createBrowserPilot({
  win,
  doc,
  enabled = false,
  projectId,
  loadInertSdk,
  readProductMarker = () => void 0,
  onDiagnostic = () => {
  }
}) {
  if (!/^[a-z0-9]{6,20}$/.test(projectId ?? "")) throw new Error("Explicit project ID required");
  if (win.clarity) throw new Error("Pre-existing Clarity integration; no pilot initialized");
  let privateInterfaceOpened = false, transportBlocked = false;
  const readState = () => {
    try {
      const api = win.Shopify?.customerPrivacy;
      const current = api?.currentVisitorConsent?.();
      const url = new URL(win.location.href);
      const canonical = doc.querySelector('link[rel="canonical"]')?.href;
      const marker = readProductMarker();
      return {
        enabled: enabled && !privateInterfaceOpened && !transportBlocked && referrerAllowed(doc.referrer) && canonical === url.href && win.navigator.globalPrivacyControl !== true && marker?.path === url.pathname && marker?.canonical === canonical && marker?.eligible === true,
        privacyApiPresent: !!api && typeof api.currentVisitorConsent === "function" && typeof api.analyticsProcessingAllowed === "function",
        analyticsConsent: current?.analytics,
        analyticsProcessingAllowed: api?.analyticsProcessingAllowed?.() === true,
        url: url.href,
        productEarlyAccess: marker?.path === url.pathname && typeof marker.earlyAccess === "boolean" ? marker.earlyAccess : void 0
      };
    } catch {
      return { enabled: false };
    }
  };
  let pilot;
  const allowed = () => {
    const state = readState();
    return pilot?.status().permitted === true && recordingPolicy(state).allowed;
  };
  let requestCount = 0;
  let report;
  const transport = createAbortableTransport({
    fetchImpl: (...args) => {
      requestCount++;
      return win.fetch(...args);
    },
    endpoint: "https://e.clarity.ms/collect",
    isAllowed: allowed,
    onStatus: () => {
      report?.();
    },
    onFatal: () => {
      transportBlocked = true;
      beforeNavigation();
    }
  });
  const clearOnlyOwnedClarityStorage = () => {
    const host = win.location.hostname;
    for (const name of ["_clck", "_clsk"]) {
      for (const domain of ["", host, "." + host]) {
        doc.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? "; Domain=" + domain : ""}`;
      }
    }
    win.sessionStorage.removeItem("_cltk");
  };
  pilot = createPilotLifecycle({
    projectId,
    readCurrentState: readState,
    loadInertSdk,
    applyStrictMaskBeforeStart: () => applyStrictDomMask(doc),
    sendAcceptedPayload: transport.send,
    abortTransports: transport.abortAll,
    clearOnlyOwnedClarityStorage
  });
  report = () => {
    const policy = recordingPolicy(readState());
    const state = readState();
    const status = pilot.status();
    onDiagnostic({
      status: status.active && status.permitted && policy.allowed ? "recording" : "blocked",
      reason: policy.allowed ? status.active ? "allowed" : "not-active" : policy.reason,
      requestCount,
      pendingRequests: transport.pendingCount(),
      ...transport.counters(),
      analyticsState: ["yes", "no"].includes(state.analyticsConsent) ? state.analyticsConsent : "unavailable",
      processingAllowed: state.analyticsProcessingAllowed === true,
      gpc: win.navigator.globalPrivacyControl === true,
      clarityCookieCount: doc.cookie.split(";").filter((cookie) => /^\s*_(?:clck|clsk)=/.test(cookie)).length
    });
  };
  const evaluate = () => {
    void pilot.sync().then(report);
    report();
  };
  const beforeNavigation = () => {
    pilot.beforeRouteChange();
    report();
  };
  const beforePrivateDomChange = () => {
    privateInterfaceOpened = true;
    beforeNavigation();
  };
  const navigate = () => {
    beforeNavigation();
    queueMicrotask(evaluate);
  };
  const restorations = [];
  try {
    for (const method of ["pushState", "replaceState"]) {
      const original = win.history[method];
      const wrapper = function(...args) {
        beforeNavigation();
        const result = original.apply(this, args);
        queueMicrotask(evaluate);
        return result;
      };
      win.history[method] = wrapper;
      if (win.history[method] !== wrapper) throw new Error("History hook not writable");
      restorations.push(() => {
        if (win.history[method] === wrapper) win.history[method] = original;
      });
    }
  } catch (error) {
    pilot.dispose();
    for (const restore of restorations) restore();
    throw error;
  }
  doc.addEventListener("visitorConsentCollected", evaluate, true);
  win.addEventListener("popstate", navigate, true);
  win.addEventListener("hashchange", navigate, true);
  const privateSelector = 'cart-drawer-component, .account-popover, .account-drawer, [aria-label="Open cart drawer"], [href^="/cart"], [href^="/account"], form[action^="/contact"], input[type="email"], input[type="password"], input[autocomplete="email"]';
  const privateInteraction = (event) => {
    if (event.type === "keydown" && !["Enter", " ", "Spacebar"].includes(event.key)) return;
    const target = event.target;
    if (event.type === "submit" || target?.closest?.(privateSelector)) beforePrivateDomChange();
  };
  for (const type of ["click", "focusin", "keydown", "submit"]) win.addEventListener(type, privateInteraction, true);
  if (doc.querySelector("cart-drawer-component dialog[open], .account-drawer dialog[open], details.account-popover[open]")) beforePrivateDomChange();
  const observer = new win.MutationObserver(() => {
    applyStrictDomMask(doc);
  });
  observer.observe(doc.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-clarity-unmask"] });
  evaluate();
  return {
    status: pilot.status,
    reevaluateAfterVerifiedRender: evaluate,
    beforePrivateDomChange,
    dispose() {
      pilot.dispose();
      observer.disconnect();
      doc.removeEventListener("visitorConsentCollected", evaluate, true);
      win.removeEventListener("popstate", navigate, true);
      win.removeEventListener("hashchange", navigate, true);
      for (const type of ["click", "focusin", "keydown", "submit"]) win.removeEventListener(type, privateInteraction, true);
      for (const restore of restorations) restore();
    }
  };
}

// shopify-main-bootstrap.mjs
function initializeShopifyPilot(win, doc, { create = createBrowserPilot, importSdk = (url) => import(url) } = {}) {
  const node = doc.getElementById("aurora-clarity-pilot-config");
  if (!node || win.__auroraClarityPilotInitialized) return;
  let config;
  try {
    config = JSON.parse(node.textContent);
  } catch {
    return;
  }
  if (String(win.Shopify?.theme?.id) !== "208253976906") return;
  if (config.liveDiagnostics === true) {
    Object.assign(doc.documentElement.dataset, { clarityPilotStatus: "blocked", clarityPilotReason: "server-scope", clarityPilotSdkImports: "0", clarityPilotRequests: "0", clarityPilotCompleted: "0", clarityPilotResponses: "0", clarityPilotHttpStatus: "none", clarityPilotPending: "0", clarityPilotPrivacyFeature: "not-needed" });
  }
  const marker = config.marker;
  if (config.version !== 1 || config.enabled !== true || typeof config.sdkUrl !== "string" || !marker || marker.path !== win.location.pathname || marker.canonical !== doc.querySelector('link[rel="canonical"]')?.href) return;
  const sdkUrl = new URL(config.sdkUrl, win.location.href);
  if (sdkUrl.protocol !== "https:" || !["cdn.shopify.com", win.location.hostname].includes(sdkUrl.hostname) || !sdkUrl.pathname.endsWith("/aurora-clarity-sdk-14175d59.js")) return;
  win.__auroraClarityPilotInitialized = true;
  let sdkImports = 0, featureRequested = false, featureStatus = "not-needed", pilot;
  const diagnostics = config.liveDiagnostics === true;
  const diagnostic = (value) => {
    if (!diagnostics) return;
    const dataset = doc.documentElement.dataset;
    dataset.clarityPilotStatus = value.status;
    dataset.clarityPilotReason = value.reason;
    dataset.clarityPilotSdkImports = String(sdkImports);
    dataset.clarityPilotRequests = String(value.requestCount);
    dataset.clarityPilotPending = String(value.pendingRequests);
    dataset.clarityPilotCompleted = String(value.completed ?? 0);
    dataset.clarityPilotResponses = String(value.responses ?? 0);
    dataset.clarityPilotHttpStatus = String(value.lastHttpStatus ?? "none");
    dataset.clarityPilotErrorName = value.lastErrorName ?? "none";
    dataset.clarityPilotCircuitOpen = String(value.circuitOpen === true);
    dataset.clarityPilotCookieCount = String(value.clarityCookieCount ?? 0);
    dataset.clarityPilotAnalytics = value.analyticsState ?? "unavailable";
    dataset.clarityPilotProcessingAllowed = String(value.processingAllowed === true);
    dataset.clarityPilotGpc = String(value.gpc === true);
    dataset.clarityPilotPrivacyFeature = featureStatus;
  };
  const currentMarker = () => {
    try {
      return JSON.parse(doc.getElementById("aurora-clarity-pilot-config")?.textContent)?.marker;
    } catch {
      return void 0;
    }
  };
  pilot = create({
    win,
    doc,
    enabled: true,
    projectId: config.projectId,
    readProductMarker: currentMarker,
    loadInertSdk: async () => {
      sdkImports++;
      const module = await importSdk(sdkUrl.href);
      return module.sdk;
    },
    onDiagnostic: diagnostic
  });
  const hasApi = () => typeof win.Shopify?.customerPrivacy?.currentVisitorConsent === "function" && typeof win.Shopify?.customerPrivacy?.analyticsProcessingAllowed === "function";
  const loadPrivacyIfNecessary = () => {
    if (hasApi()) {
      pilot.reevaluateAfterVerifiedRender();
      return;
    }
    if (featureRequested || typeof win.Shopify?.loadFeatures !== "function") return;
    featureRequested = true;
    featureStatus = "requested";
    try {
      win.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
        featureStatus = error || !hasApi() ? "unavailable" : "available";
        pilot.reevaluateAfterVerifiedRender();
      });
    } catch {
      featureStatus = "unavailable";
      pilot.reevaluateAfterVerifiedRender();
    }
  };
  if (!win.location.search && !win.location.hash && referrerAllowed(doc.referrer)) loadPrivacyIfNecessary();
  return { reevaluate: () => {
    loadPrivacyIfNecessary();
    pilot.reevaluateAfterVerifiedRender();
  }, dispose: () => pilot.dispose() };
}
if (typeof window !== "undefined" && typeof document !== "undefined") initializeShopifyPilot(window, document);
export {
  initializeShopifyPilot
};
